// src/ui/overlays/SpectatorBanner.tsx — P7 Plan 09 (UI-05, D-08/09).
//
// Minimal spectator HUD. After the local player is caught the camera follows a
// free hider (GameScene owns the camera; Plan 10 wires App.tsx) and this banner
// explains *why* the player can no longer move (D-09). Standalone + self-gating
// (RoundTimer idiom): mounts unconditionally in the game subtree, returns null
// unless sim-tick reports playerStatus === 'caught'.
//
// WARNING-15 tier-isolation: subscribes ONLY to the renderBus 'sim-tick'
// payload. The watched hider's display name is resolved render-side in
// GameScene (07-07) and arrives on `spectatorWatching`; zero sim-tier imports.
//
// Source: 07-UI-SPEC §Surface 6; PATTERNS.md SpectatorBanner analog (RoundTimer
// self-gate + TeamSwitchOverlay aria).

import { useEffect, useRef, useState, type JSX } from 'react';

import { renderBus } from '../../render/bus';
import type { EntityStatus } from '../../shared/types/world';

import './spectator-banner.css';

// The advance hint fades after 3 s and shows once per spectate session
// (07-UI-SPEC §Surface 6 — "first 3 seconds … not shown again that session").
const HINT_MS = 3000;

interface SpectatorState {
  status: EntityStatus;
  watchedName: string | null;
  free: number;
  caught: number;
}

/**
 * Split the per-entity status snapshot into a free/caught hider tally. In-play
 * hiders (free / hiding / peeking) count as free; removed hiders (caught /
 * escorting / spectating) count as caught. GameScene populates `entityStatuses`
 * with the hider set at emit time (Plan 10), so hunters do not inflate the
 * free count.
 */
function classifyCounts(
  statuses: Record<string, EntityStatus> | undefined,
): { free: number; caught: number } {
  let free = 0;
  let caught = 0;
  if (statuses) {
    for (const s of Object.values(statuses)) {
      if (s === 'free' || s === 'hiding' || s === 'peeking') free += 1;
      else if (s === 'caught' || s === 'escorting' || s === 'spectating') caught += 1;
    }
  }
  return { free, caught };
}

export function SpectatorBanner(): JSX.Element | null {
  const [state, setState] = useState<SpectatorState | null>(null);
  const [hintVisible, setHintVisible] = useState(false);
  const hintShownRef = useRef(false);

  useEffect(() => {
    return renderBus.on('sim-tick', (e) => {
      const { free, caught } = classifyCounts(e.entityStatuses);
      setState({
        status: e.playerStatus,
        watchedName: e.spectatorWatching?.name ?? null,
        free,
        caught,
      });
    });
  }, []);

  const isCaught = state?.status === 'caught';

  // First spectate this session: flash the "Tap to switch" hint, then auto-fade.
  useEffect(() => {
    if (!isCaught || hintShownRef.current) return;
    hintShownRef.current = true;
    setHintVisible(true);
    const t = window.setTimeout(() => setHintVisible(false), HINT_MS);
    return (): void => window.clearTimeout(t);
  }, [isCaught]);

  if (!state || state.status !== 'caught') return null;

  return (
    <div
      className="spectator-banner"
      data-testid="spectator-banner"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="spectator-banner__line">
        You&apos;re caught — watching{' '}
        <span
          className="spectator-banner__name"
          data-testid="spectator-name"
          // Inline amber mirrors spectator-banner.css for jsdom (PATTERNS E).
          style={{ color: '#e8a838' }}
        >
          {state.watchedName ?? '…'}
        </span>
      </span>
      <span
        className="spectator-banner__count"
        data-testid="spectator-count"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {state.free} free · {state.caught} caught
      </span>
      {hintVisible && (
        <span className="spectator-banner__hint" data-testid="spectator-hint">
          Tap to switch
        </span>
      )}
    </div>
  );
}
